import React, { useRef, useEffect, useCallback, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { KanbanCard } from '../HBKanban.types';
import HBKanbanCard from '../HBKanbanCard/HBKanbanCard';
import HBKanbanInfiniteScroll from '../HBKanbanInfiniteScroll/HBKanbanInfiniteScroll';
import HBLoader from '../../HBLoader/HBLoader';
import { LoaderContainer } from './HBKanbanColumn.styles';
import spacing from '../../../styles/spacing';

interface VirtualizedCardListProps<TItemData = any> {
    items: KanbanCard<TItemData>[];
    scrollElementRef: React.RefObject<HTMLDivElement>;
    infiniteScroll?: {
        onLoadMore: () => void;
        hasMore: boolean;
        isLoading: boolean;
        testIdName?: string;
        columnId: string;
    };
}

const DEFAULT_CARD_HEIGHT = 120;
const OVERSCAN = 5; // Number of items to render outside viewport
const GAP = 12; // Gap between items (spacing[12] = 12px) - matches StyledColumnItems gap
const PADDING = spacing[4]; // Padding from container - matches StyledColumnItems padding

export const VirtualizedCardList = <TItemData = any,>({
    items,
    scrollElementRef,
    infiniteScroll
}: VirtualizedCardListProps<TItemData>) => {
    // Cache for measured heights
    const heightCacheRef = useRef<Map<string, number>>(new Map());
    const [isMounted, setIsMounted] = useState(false);

    // Mark as mounted after first render to trigger initial measurement
    useEffect(() => {
        setIsMounted(true);
    }, []);

    // Estimate size function - returns ONLY wrapper height (no gap)
    // We'll use the gap option which handles spacing automatically
    const estimateSize = useCallback(
        (index: number) => {
            const item = items[index];
            if (!item) {
                return DEFAULT_CARD_HEIGHT;
            }
            const cachedHeight = heightCacheRef.current.get(item.id);
            // Return only wrapper height - gap is handled by virtualizer's gap option
            return cachedHeight || DEFAULT_CARD_HEIGHT;
        },
        [items]
    );

    // Virtualizer instance
    const virtualizer = useVirtualizer({
        count: items.length,
        getScrollElement: () => scrollElementRef.current,
        estimateSize,
        overscan: OVERSCAN,
        // Use virtualizer's built-in gap handling
        gap: GAP
    });

    // Track previous items count to detect when new items are added
    const previousItemsCountRef = useRef(items.length);
    const remeasureTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const wrapperElementMapRef = useRef<Map<HTMLElement, string>>(new Map());
    const observerRef = useRef<ResizeObserver | null>(null);
    const pendingMeasurementsRef = useRef<Set<string>>(new Set());
    const initialMeasurementTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Use ResizeObserver to observe wrapper elements directly
    // This ensures we measure the actual rendered height that virtualizer will use
    useEffect(() => {
        if (!isMounted || !scrollElementRef.current) {
            return;
        }

        // Store refs at the start of effect to avoid stale closure issues in cleanup
        const wrapperElementMap = wrapperElementMapRef.current;
        const pendingMeasurements = pendingMeasurementsRef.current;

        const observer = new ResizeObserver(entries => {
            let needsRemeasure = false;

            entries.forEach(entry => {
                const wrapperElement = entry.target as HTMLElement;
                const itemId = wrapperElementMap.get(wrapperElement);

                if (!itemId) {
                    return;
                }

                // Measure the wrapper's actual height (which should equal card height)
                const wrapperHeight = entry.contentRect.height;

                if (wrapperHeight > 0) {
                    const cachedHeight = heightCacheRef.current.get(itemId);

                    // Update cache if height changed significantly (more than 1px)
                    if (
                        !cachedHeight ||
                        Math.abs(cachedHeight - wrapperHeight) > 1
                    ) {
                        heightCacheRef.current.set(itemId, wrapperHeight);
                        needsRemeasure = true;
                        // Measure wrapper for virtualizer
                        // Note: virtualizer expects height + gap, but we measure just height
                        // So we need to tell it the total size (height + gap)
                        virtualizer.measureElement(wrapperElement);
                    }
                }
            });

            // Debounce remeasure to batch multiple resize events
            if (needsRemeasure) {
                if (remeasureTimeoutRef.current) {
                    clearTimeout(remeasureTimeoutRef.current);
                }
                remeasureTimeoutRef.current = setTimeout(() => {
                    virtualizer.measure();
                }, 0);
            }
        });

        // Store observer in ref so it's accessible in ref callbacks
        observerRef.current = observer;

        // Observe all wrapper elements (not cards)
        const observeWrappers = () => {
            if (!scrollElementRef.current) {
                return;
            }

            const wrapperElements = scrollElementRef.current.querySelectorAll(
                '[data-virtual-index]'
            ) as NodeListOf<HTMLElement>;

            wrapperElements.forEach(wrapperElement => {
                // Skip if already being observed
                if (wrapperElementMap.has(wrapperElement)) {
                    return;
                }

                const virtualIndex =
                    wrapperElement.getAttribute('data-virtual-index');
                if (virtualIndex !== null) {
                    const index = parseInt(virtualIndex, 10);
                    const item = items[index];
                    if (item) {
                        wrapperElementMap.set(wrapperElement, item.id);
                        observer.observe(wrapperElement);

                        // Measure immediately when observing
                        // ResizeObserver will handle updates and trigger remeasure if needed
                        const wrapperHeight =
                            wrapperElement.getBoundingClientRect().height;
                        if (wrapperHeight > 0) {
                            const cachedHeight = heightCacheRef.current.get(
                                item.id
                            );
                            if (
                                !cachedHeight ||
                                Math.abs(cachedHeight - wrapperHeight) > 1
                            ) {
                                heightCacheRef.current.set(
                                    item.id,
                                    wrapperHeight
                                );
                                // Update virtualizer with current height
                                virtualizer.measureElement(wrapperElement);

                                // Mark measurement as complete
                                pendingMeasurements.delete(item.id);

                                // If this was the last pending measurement, trigger full remeasure
                                if (pendingMeasurements.size === 0) {
                                    // Clear any existing timeout
                                    if (initialMeasurementTimeoutRef.current) {
                                        clearTimeout(
                                            initialMeasurementTimeoutRef.current
                                        );
                                    }
                                    // Trigger remeasure after a short delay to batch all measurements
                                    initialMeasurementTimeoutRef.current =
                                        setTimeout(() => {
                                            virtualizer.measure();
                                        }, 0);
                                }
                            }
                        }
                    }
                }
            });
        };

        // Track all items as pending measurements initially
        items.forEach(item => {
            if (!heightCacheRef.current.has(item.id)) {
                pendingMeasurements.add(item.id);
            }
        });

        // Initial observation - use double RAF to ensure DOM is ready
        const rafId = requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                observeWrappers();
            });
        });

        // Re-observe when items change (pagination)
        const itemsChanged = previousItemsCountRef.current !== items.length;
        const previousCount = previousItemsCountRef.current;
        previousItemsCountRef.current = items.length;

        if (itemsChanged && items.length > previousCount) {
            // New items were added - track them for measurement
            const newItems = items.slice(previousCount);
            newItems.forEach(item => {
                pendingMeasurements.add(item.id);
            });

            // Don't disconnect - keep observing existing elements
            // Just re-observe to catch new elements after DOM updates
            // Use triple RAF to ensure DOM is fully updated and all items are rendered
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        // Observe all wrappers (including new ones)
                        observeWrappers();
                    });
                });
            });
        }

        return () => {
            cancelAnimationFrame(rafId);
            observer.disconnect();
            observerRef.current = null;
            // Use the refs stored at the start of the effect
            wrapperElementMap.clear();
            pendingMeasurements.clear();
            if (remeasureTimeoutRef.current) {
                clearTimeout(remeasureTimeoutRef.current);
            }
            if (initialMeasurementTimeoutRef.current) {
                clearTimeout(initialMeasurementTimeoutRef.current);
            }
        };
    }, [virtualizer, items, scrollElementRef, isMounted]);

    const virtualItems = virtualizer.getVirtualItems();
    const totalSize = virtualizer.getTotalSize();

    // Virtualized list items doesn't push the loader, so we add margin above the loader to match the position of regular loaders
    const EXTRA_MARGIN_TOP = 21;

    return (
        <div
            style={{
                height: totalSize || items.length * (DEFAULT_CARD_HEIGHT + GAP),
                position: 'relative',
                width: '100%',
                paddingTop: `${PADDING}px`,
                paddingLeft: `${PADDING}px`,
                paddingRight: `${PADDING}px`,
                paddingBottom: `${PADDING}px`
            }}
        >
            {/* Render visible items */}
            {virtualItems.length > 0
                ? virtualItems.map(virtualItem => {
                      const item = items[virtualItem.index];
                      if (!item) {
                          return null;
                      }

                      // Use cached height if available, otherwise use virtualItem.size (estimate)
                      // This allows cards to render at their natural height initially, then update after measurement
                      const cachedHeight = heightCacheRef.current.get(item.id);

                      // Ref callback for measuring - virtualizer tracks elements via this ref
                      const measureRef = (element: HTMLElement | null) => {
                          if (!element) {
                              return;
                          }

                          // Register element for ResizeObserver tracking
                          if (!wrapperElementMapRef.current.has(element)) {
                              wrapperElementMapRef.current.set(
                                  element,
                                  item.id
                              );

                              // Track this measurement as pending
                              pendingMeasurementsRef.current.add(item.id);

                              // Start observing for future size changes
                              if (observerRef.current) {
                                  observerRef.current.observe(element);
                              }
                          }

                          // Measure with virtualizer - it will measure the element's bounding box
                          // Use double RAF to ensure element is fully rendered
                          requestAnimationFrame(() => {
                              requestAnimationFrame(() => {
                                  const wrapperHeight =
                                      element.getBoundingClientRect().height;
                                  if (wrapperHeight > 0) {
                                      const currentCached =
                                          heightCacheRef.current.get(item.id);
                                      if (
                                          !currentCached ||
                                          Math.abs(
                                              currentCached - wrapperHeight
                                          ) > 1
                                      ) {
                                          heightCacheRef.current.set(
                                              item.id,
                                              wrapperHeight
                                          );

                                          // Set explicit height on element BEFORE measuring
                                          // This ensures virtualizer measures the correct size
                                          element.style.height = `${wrapperHeight}px`;

                                          // Now measure with virtualizer - it will use the explicit height
                                          virtualizer.measureElement(element);

                                          // Immediately trigger remeasure to update positions
                                          // This ensures virtualItem.size reflects the measured height
                                          virtualizer.measure();

                                          // Mark measurement as complete
                                          pendingMeasurementsRef.current.delete(
                                              item.id
                                          );

                                          // If this was the last pending measurement, trigger one more remeasure
                                          // to ensure all positions are final
                                          if (
                                              pendingMeasurementsRef.current
                                                  .size === 0
                                          ) {
                                              // Clear any existing timeout
                                              if (
                                                  initialMeasurementTimeoutRef.current
                                              ) {
                                                  clearTimeout(
                                                      initialMeasurementTimeoutRef.current
                                                  );
                                              }
                                              // Final remeasure after all measurements complete
                                              initialMeasurementTimeoutRef.current =
                                                  setTimeout(() => {
                                                      virtualizer.measure();
                                                  }, 0);
                                          }
                                      } else {
                                          // Already cached, just mark as complete
                                          pendingMeasurementsRef.current.delete(
                                              item.id
                                          );
                                      }
                                  }
                              });
                          });
                      };

                      return (
                          <div
                              key={item.id}
                              data-index={virtualItem.index}
                              data-virtual-index={virtualItem.index}
                              ref={measureRef}
                              style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: '100%',
                                  // Only set explicit height if we have a cached measurement
                                  // Otherwise let it render naturally so we can measure it
                                  ...(cachedHeight
                                      ? { height: `${cachedHeight}px` }
                                      : {}),
                                  transform: `translateY(${virtualItem.start}px)`
                              }}
                          >
                              <div
                                  data-kanban-card
                                  style={{
                                      // Ensure no margins/padding that would affect wrapper height
                                      margin: 0,
                                      padding: 0,
                                      ...(cachedHeight
                                          ? { height: '100%' }
                                          : {})
                                  }}
                              >
                                  <HBKanbanCard card={item} />
                              </div>
                          </div>
                      );
                  })
                : null}

            {/* Infinite scroll sentinel - positioned after all items */}
            {infiniteScroll && (
                <div
                    style={{
                        position: 'absolute',
                        top: totalSize,
                        left: 0,
                        width: '100%',
                        paddingLeft: `${PADDING}px`,
                        paddingRight: `${PADDING}px`,
                        marginTop: `${GAP + EXTRA_MARGIN_TOP}px`
                    }}
                >
                    <HBKanbanInfiniteScroll
                        onLoadMore={infiniteScroll.onLoadMore}
                        hasMore={infiniteScroll.hasMore}
                        isLoading={infiniteScroll.isLoading}
                        testIdName={infiniteScroll.testIdName}
                        columnId={infiniteScroll.columnId}
                    >
                        <LoaderContainer>
                            <HBLoader
                                testId="kanban-load-more--loader"
                                size="xs"
                            />
                        </LoaderContainer>
                    </HBKanbanInfiniteScroll>
                </div>
            )}
        </div>
    );
};
